// src/pages/Team/index.jsx
// STATUS: active | UPDATED: 2026-09-27
// LOG:
//   2026-09-27  A manager's floor snapshot counts salespeople only. See 0047.
//   2026-09-26  Faces above the roster. The roster is sorted by email, which is
//               how you look one person up and not how you look at everybody.
//               An owner gets his managers and his floor as avatars and clicks
//               through to one person. Every row in the roster is now a link to
//               the same place.
//   2026-08-27  Permission now comes from the viewer's role, not from whether
//               any rows came back. canSeeProgress was derived from
//               progressRows.length > 0, which conflated "you may not see this"
//               with "there is nothing yet": a manager whose team genuinely had
//               no progress saw the member view and reasonably concluded the
//               product was broken. A manager now sees the bars at zero, which
//               is the true and more useful answer.
//   2026-08-29  Floor numbers between the sign-off queue and the roster. The
//               page now reads work, then measure, then reference, which is the
//               order a manager actually needs them in.
//   2026-08-29  Pending sign-offs sit above the roster, because that is the
//               thing a manager came here to do. The roster is reference; the
//               queue is work, and work goes first.
//   2026-08-28  Moved from pages/Team.jsx to pages/Team/index.jsx so the
//               invite dialog can sit beside it, matching Coach, Dashboard and
//               Settings. A Team.jsx file next to a Team/ folder resolves by
//               luck rather than intent.
//   2026-08-02  Rewritten from placeholder stats to a live roster. Reads real
//               users and step_progress from Supabase under RLS: members see
//               teammates, managers and admins additionally see progress.
// NEXT: Manager sign-off actions per member (Phase 3)
// The team board. Every row is a real person from the database with their
// real position on the Roadmap. No fake zeros anywhere, but a true zero is not
// a fake zero and a manager needs to see it.

import { useEffect, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import {
  Box, Flex, HStack, VStack, Text, Icon, Spinner, Progress, Badge,
} from '@chakra-ui/react'
import { Users } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../hooks/useAuth'
import FloorView from './FloorView'
import { displayNameFor } from '../../lib/names.js'
import { STEPS } from '../../content/steps'
import { roleLabel, canSeeTeamProgress } from '../../lib/roles'
import InviteMember from './InviteMember.jsx'
import PendingSignoffs from './PendingSignoffs.jsx'
import FloorSnapshot from './FloorSnapshot.jsx'
import FloorBoard from './FloorBoard.jsx'
import OwnerSnapshot from './OwnerSnapshot'
import OwnerDisciplines from './OwnerDisciplines.jsx'
import Faces from './Faces.jsx'


function MemberRow({ member, linked }) {
  const name = displayNameFor(member, { email: member.email })
  const initial = (name || '?')[0].toUpperCase()
  const pct = Math.round(((member.passedCount || 0) / STEPS.length) * 100)

  return (
    <HStack
      bg="card" border="1px solid" borderColor="line" borderRadius="card" p={4} spacing={4}
      {...(linked
        ? {
            // A row that goes somewhere should say so before it is tapped.
            as: RouterLink,
            to: `/team/${member.id}/`,
            _hover: { borderColor: 'lineStrong', textDecoration: 'none' },
            _focusVisible: { outline: '2px solid', outlineColor: 'accent.500', outlineOffset: '2px' },
            transition: 'border-color 140ms ease',
          }
        : {})}
    >
      <Flex boxSize="40px" borderRadius="full" bg="ink" color="onInk" align="center" justify="center" fontWeight={600} flexShrink={0}>
        {initial}
      </Flex>
      <Box flex={1} minW={0}>
        <HStack spacing={2}>
          <Text fontWeight={600} noOfLines={1} letterSpacing="-0.01em">
            {name}
          </Text>
          {member.role && (
            <Badge bg="surface" color="inkMuted" fontWeight={500} fontSize="10px" px={2} py={0.5} borderRadius="pill" textTransform="uppercase" letterSpacing="0.06em">
              {roleLabel(member.role, member.job_function)}
            </Badge>
          )}
        </HStack>
        {member.hasProgress ? (
          <HStack spacing={3} mt={1.5}>
            <Progress
              value={pct}
              size="xs"
              flex={1}
              maxW="220px"
              borderRadius="full"
              bg="surface2"
              sx={{ '& > div': { background: 'var(--chakra-colors-ink)' } }}
            />
            <Text fontSize="mono-xs" fontFamily="mono" color="inkMuted" flexShrink={0}>
              {member.passedCount}/{STEPS.length}
            </Text>
          </HStack>
        ) : (
          <Text fontSize="xs" color="inkDim" mt={1}>
            {member.email}
          </Text>
        )}
      </Box>
    </HStack>
  )
}

export default function Team() {
  const { role, user, profile } = useAuth()
  const canSeeProgress = canSeeTeamProgress(role)
  const [state, setState] = useState({ loading: true, members: [], waitingIds: new Set() })
  // Bumped after an invite so the roster refetches and the new person appears
  // without asking anyone to reload the page.
  const [reloadToken, setReloadToken] = useState(0)

  useEffect(() => {
    let mounted = true
    async function load() {
      try {
        const timeout = new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 6000))
        const { data: users, error } = await Promise.race([
          // Otto has a users row by design, since 18 Sep, so he can send as
          // otto@. `is_agent` exists precisely so he is never counted as a
          // person, and Admin and AdminTeam already filter on it. This roster
          // was the one place that did not, so he appeared on the team page as
          // a member of the store.
          supabase.from('users').select('*').eq('is_agent', false).order('email'),
          timeout,
        ])
        if (error || !users) throw error || new Error('no data')

        const { data: progressRows } = await supabase
          .from('step_progress')
          .select('user_id, status')

        const passedByUser = {}
        for (const row of progressRows || []) {
          if (row.status === 'check_passed' || row.status === 'signed_off') {
            passedByUser[row.user_id] = (passedByUser[row.user_id] || 0) + 1
          }
        }
        // Who is held up by us. One query rather than a prop drilled down
        // from the queue, because the faces render above it and an empty ring
        // that fills in a second later reads as a flicker rather than a fact.
        const { data: pending } = await supabase
          .from('manager_signoffs')
          .select('user_id')
          .eq('status', 'requested')
        const waitingIds = new Set((pending || []).map((r) => r.user_id))

        const members = users.map((u) => ({
          ...u,
          passedCount: passedByUser[u.id] || 0,
          hasProgress: canSeeProgress,
        }))
        if (mounted) setState({ loading: false, members, waitingIds })
      } catch {
        if (mounted) setState({ loading: false, members: [], waitingIds: new Set() })
      }
    }
    load()
    return () => {
      mounted = false
    }
  }, [canSeeProgress, reloadToken])

  return (
    <VStack align="stretch" spacing={6} maxW="sheet">
      <Flex justify="space-between" align="flex-end" gap={4} wrap="wrap">
        <Box>
          <Text textStyle="pageEyebrow" mb={1}>
            Your dealership
          </Text>
          <Text textStyle="pageTitle">
            Team
          </Text>
        </Box>
        <InviteMember onInvited={() => setReloadToken((n) => n + 1)} />
      </Flex>

      {/* Only somebody who can sign off has a queue. RLS returns an empty list
          to everyone else anyway, but rendering the heading to a salesperson
          who can never have one is just noise. */}
      {/* WHO IS IN THE STORE, before anything measured about them. This is the
          only block on the page that answers "show me my people" rather than
          "show me a number about my people", and it is the one an owner opens
          the page for. */}
      {canSeeProgress && !state.loading && (
        <Faces members={state.members} role={role} waitingIds={state.waitingIds} />
      )}

      {canSeeProgress && <PendingSignoffs members={state.members} />}

      {/* WHO IS PUTTING THE WORK IN. Above FloorView because it answers the
          question a manager actually walks in with, which is who to go and talk
          to. It scores process rather than deliveries, which is what Jazz asks
          a manager to recognise, and it has no last place. See FloorBoard.jsx.

          Manager and above only. A public ranking of voluntary behaviour gets
          gamed in a week, and practice being voluntary is the whole reason it
          is an honest signal. */}
      {canSeeProgress && !state.loading && state.members.length > 0 && (
        <FloorBoard members={state.members.filter((m) => m.role === 'employee')} />
      )}

      {/* The floor, above the roster and below the queue. Everything in it is
          about a step or about the whole floor. The one place a person is named
          is as somebody worth asking, never as somebody behind. */}
      <FloorView profile={profile} user={user} members={state.members} />

      {/* Measurement sits under the queue and above the roster. The queue is
          work, this is the answer to "is the floor moving", and the roster is
          reference you scroll to when you already know who you are looking for. */}
      {/* A manager is asking who is waiting on them. An owner is asking whether
          the store is getting better and whether the floor is being run.
          Different question, different screen. */}
      {canSeeProgress &&
        !state.loading &&
        (role === 'owner' ? (
          <>
            {/* ABOVE the sign off snapshot. Sign offs are one manager action
                and there are now four, so the wider question comes first and
                the snapshot stays as the argument for why sign offs in
                particular matter. */}
            <OwnerDisciplines />
            <OwnerSnapshot members={state.members} />
          </>
        ) : (
          // The floor only. It counted the owner and every manager as people
          // who could be "quiet" or behind on steps, and since 0047 a manager
          // cannot see those rows at all, so they would read as quiet whatever
          // they did.
          <FloorSnapshot members={state.members.filter((m) => m.role === 'employee')} />
        ))}

      {state.loading ? (
        <Flex justify="center" py={16}>
          <Spinner size="lg" color="ink" thickness="3px" />
        </Flex>
      ) : state.members.length > 0 ? (
        <VStack align="stretch" spacing={3}>
          {state.members.map((m) => (
            <MemberRow key={m.id} member={m} linked={canSeeProgress} />
          ))}
        </VStack>
      ) : (
        <Box bg="card" border="1px dashed" borderColor="lineStrong" borderRadius="cardLg" p={{ base: 8, md: 12 }} textAlign="center">
          <Flex boxSize="52px" borderRadius="full" bg="surface" align="center" justify="center" mx="auto" mb={4}>
            <Icon as={Users} boxSize={5} color="inkMuted" />
          </Flex>
          <Text fontWeight={600} mb={2} letterSpacing="-0.01em">
            Your team shows up here.
          </Text>
          <Text fontSize="body-sm" color="inkMuted" maxW="380px" mx="auto" lineHeight={1.7}>
            As members of your dealership join the Academy, every person appears on this board with their real position on the Roadmap. Managers see progress; members see teammates.
          </Text>
          <Flex justify="center" mt={5}>
            <InviteMember onInvited={() => setReloadToken((n) => n + 1)} />
          </Flex>
        </Box>
      )}

      <Box bg="surface" p={5} borderRadius="card">
        <Text fontSize="body-sm" color="inkMuted" lineHeight={1.7}>
          Sign-offs and floor numbers are live. Assignments and the daily process audit from the Trainer Edition come next.
        </Text>
      </Box>
    </VStack>
  )
}
